import React, { useEffect } from 'react';

// Shared design tokens + primitives for the admin pages, matching the visual
// language of StaffHome and ChatSessionsList (inline-styled, Apple-ish).
// Intentionally NOT Semantic UI components (except Icon/Table/Loader, which the
// reference pages keep) so buttons/badges/modals/tabs read consistently.

export const ui = {
  text: '#1d1d1f',
  muted: '#86868b',
  blue: '#0091FF', // bright CTA blue
  accent: '#007AFF', // icon/accent blue
  chip: '#F2F2F7', // light chip / neutral button bg
  field: '#F5F5F7', // input background
  pageBg: '#f9fafb',
  border: 'rgba(0,0,0,0.05)',
  headerBorder: 'rgba(34,36,38,.15)',
  greenBg: '#D1F2E1', greenFg: '#1A7F4B',
  redBg: '#FFE5E5', redFg: '#C0392B',
  amberBg: '#FFF3E0', amberFg: '#B25E00',
  grayBg: '#E5E5EA', grayFg: '#636366',
  blueBg: '#E5F0FF',
};

export const cardStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  border: `1px solid ${ui.border}`,
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  overflow: 'hidden',
};

export const thStyle = {
  textAlign: 'left',
  padding: '11px 16px',
  borderBottom: `1px solid ${ui.headerBorder}`,
  color: ui.muted,
  fontSize: '12px',
  fontWeight: 600,
  background: 'white',
  whiteSpace: 'nowrap',
};

export const tdStyle = {
  padding: '11px 16px',
  borderBottom: `1px solid ${ui.border}`,
  fontSize: '14px',
  color: ui.text,
  verticalAlign: 'middle',
};

export const fieldStyle = {
  width: '100%',
  backgroundColor: ui.field,
  borderRadius: '10px',
  border: '1px solid transparent',
  padding: '11px 14px',
  fontSize: '14px',
  fontFamily: 'inherit',
  color: ui.text,
  boxSizing: 'border-box',
  outline: 'none',
};

export const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: ui.text,
};

const PILL_VARIANTS = {
  primary: { background: ui.blue, color: 'white' },
  neutral: { background: ui.chip, color: ui.text },
  danger: { background: ui.redBg, color: ui.redFg },
  success: { background: ui.greenBg, color: ui.greenFg },
  amber: { background: ui.amberBg, color: ui.amberFg },
  ghost: { background: 'transparent', color: ui.muted },
};

export function Pill({ variant = 'primary', children, style, disabled, ...props }) {
  return (
    <button
      {...props}
      disabled={disabled}
      style={{
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: '999px',
        padding: '8px 16px',
        fontSize: '13px',
        fontWeight: 600,
        fontFamily: 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        lineHeight: 1,
        opacity: disabled ? 0.45 : 1,
        transition: 'filter .15s ease',
        ...PILL_VARIANTS[variant],
        ...style,
      }}
    >
      {children}
    </button>
  );
}

const BADGE_TONES = {
  neutral: { background: ui.chip, color: ui.text },
  green: { background: ui.greenBg, color: ui.greenFg },
  red: { background: ui.redBg, color: ui.redFg },
  amber: { background: ui.amberBg, color: ui.amberFg },
  gray: { background: ui.grayBg, color: ui.grayFg },
  blue: { background: ui.blueBg, color: ui.accent },
};

export function Badge({ tone = 'neutral', children, style }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        padding: '3px 10px',
        ...BADGE_TONES[tone],
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export function Select({ style, children, ...props }) {
  return (
    <select
      {...props}
      style={{ ...fieldStyle, height: '40px', cursor: 'pointer', paddingRight: '28px', ...style }}
    >
      {children}
    </select>
  );
}

// Lightweight modal: fixed overlay + centered card, closes on backdrop click or Escape.
export function Modal({ open, onClose, title, children, footer, width = 460 }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '20px',
          width: '100%',
          maxWidth: width,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        }}
      >
        {title && (
          <div
            style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${ui.border}`,
              fontSize: '16px',
              fontWeight: 700,
              color: ui.text,
            }}
          >
            {title}
          </div>
        )}
        <div style={{ padding: '24px' }}>{children}</div>
        {footer && (
          <div
            style={{
              padding: '16px 24px',
              borderTop: `1px solid ${ui.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              background: '#fafafa',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// Explanatory banner describing what a page/tab lets the user do.
export function InfoNote({ children, style }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        background: ui.blueBg,
        border: `1px solid ${ui.accent}22`,
        borderRadius: '12px',
        padding: '12px 16px',
        marginBottom: '16px',
        fontSize: '13px',
        lineHeight: 1.5,
        color: '#1d3a5f',
        ...style,
      }}
    >
      {children}
    </div>
  );
}
